import {Component, computed, ElementRef, effect, inject, input, InputSignal, linkedSignal, OutputEmitterRef, Signal, ChangeDetectionStrategy, output, WritableSignal} from '@angular/core';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {DomSanitizer, SafeHtml, SafeUrl} from '@angular/platform-browser';
import {catchError, filter, isObservable, map, merge, Observable, of, share, startWith, switchMap, takeUntil, timer} from 'rxjs';
import {Icon} from '../../types/icons/icon.type';
import {IconUrlInput} from '../../types/icon-url/icon-url-input.type';
import {IconUrlValue} from '../../types/icon-url/icon-url-value.type';
import {UrlState} from '../../types/url-state.type';
import {AppIconSizeEnum} from '../../enums/app-icon-size.enum';
import {IconService} from '../../services/icon/icon.service';
import {IMAGE_PATH} from '../../constants/image-paths.constants';
import {isIconUrlConfig} from '../../util/is-icon-url-config/is-icon-url-config.util';
import {DefaultIcon} from '../../types/icons/default-icon.type';
import {LOADING_SPINNER_THRESHOLD_IN_MS} from '../../constants/ui/loading-spinner-threshold-in-ms.constant';
import {DEFAULT_ICON_SIZE_CUSTOM_CSS_PROPERTY_NAME} from '../../constants/ui/default-icon-size-custom-css-property-name.constant';
import {LoadingState} from '../../types/icons/icon-loading-state.type';

@Component({
    selector: 'app-icon',
    templateUrl: './icon.component.html',
    imports: [],
    styleUrls: ['./icon.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    host: { '[style]': 'style()' }
})
export class IconComponent {
    public svgIcon: InputSignal<Icon | undefined> = input<Icon | undefined>();
    public iconUrl: InputSignal<IconUrlValue> = input<IconUrlValue>();
    public iconSize: InputSignal<AppIconSizeEnum> = input<AppIconSizeEnum>(AppIconSizeEnum.INHERIT);
    public qaTag: InputSignal<string | undefined> = input<string>();
    public shouldDisplayLoadingSpinner: InputSignal<boolean> = input<boolean>(true);
    public loadingSpinnerThreshold: InputSignal<number> = input<number>(LOADING_SPINNER_THRESHOLD_IN_MS);
    public loadingChange: OutputEmitterRef<boolean> = output<boolean>();
    protected readonly style: Signal<string>;
    protected readonly svgHtml: Signal<SafeHtml>;
    protected readonly fallbackIconHtml: Signal<SafeHtml>;
    protected readonly loadingSpinnerHtml: Signal<SafeHtml>;
    protected readonly isLoading: Signal<boolean>;
    protected readonly isLoadingSpinnerVisible: Signal<boolean>;
    protected readonly resolvedIconUrl: Signal<string | SafeUrl | undefined>;
    private readonly hostElement = inject(ElementRef);
    private readonly iconService: IconService = inject(IconService);
    private readonly domSanitizer: DomSanitizer = inject(DomSanitizer);
    private readonly iconUrlState: Signal<UrlState>;
    private readonly svgLoadingState: Signal<LoadingState>;
    private readonly isImageLoading: WritableSignal<boolean>;
    private readonly NOT_LOADING_STATE: LoadingState = {
        isLoading: false,
        isLoadingSpinnerVisible: false
    };

    public constructor() {
        this.style = computed(() => {
            const iconSize: AppIconSizeEnum = this.iconSize();
            if (iconSize && iconSize !== AppIconSizeEnum.INHERIT) {
                if (iconSize === AppIconSizeEnum.CSS_VARIABLE) {
                    const iconSizeFromCustomProps = this.getIconSizeFromCustomCSSProperty();
                    return `width: ${iconSizeFromCustomProps}px; height: ${iconSizeFromCustomProps}px`;
                }
                return `width: ${iconSize}; height: ${iconSize}`;
            }
            return '';
        });

        this.svgHtml = toSignal(
            toObservable(this.svgIcon)
                .pipe(
                    filter((name): name is Icon => name !== undefined),
                    switchMap(name => this.iconService.getSvg(name)),
                    map(svg => this.domSanitizer.bypassSecurityTrustHtml(svg))
                ),
            { initialValue: '' as unknown as SafeHtml }
        );

        this.svgLoadingState = toSignal(
            toObservable(this.svgIcon)
                .pipe(
                    switchMap((name): Observable<LoadingState> => {
                        if (!name) {
                            return of(this.NOT_LOADING_STATE);
                        }
                        const loaded$ = this.iconService.getSvg(name)
                            .pipe(
                                map((): LoadingState => this.NOT_LOADING_STATE),
                                catchError(() => of(this.NOT_LOADING_STATE)),
                                share()
                            );
                        return merge(
                            timer(this.loadingSpinnerThreshold())
                                .pipe(
                                    map((): LoadingState => ({
                                        isLoading: true,
                                        isLoadingSpinnerVisible: true
                                    })),
                                    takeUntil(loaded$)
                                ),
                            loaded$
                        )
                            .pipe(startWith({
                                isLoading: true,
                                isLoadingSpinnerVisible: false
                            } as LoadingState));
                    })
                ),
            { initialValue: this.NOT_LOADING_STATE }
        );

        this.fallbackIconHtml = toSignal(
            toObservable(this.iconUrl)
                .pipe(
                    map(value => isIconUrlConfig(value) ? value.fallback : undefined),
                    filter((name): name is DefaultIcon => name !== undefined),
                    switchMap(name => this.iconService.getSvg(name)),
                    map(svg => this.domSanitizer.bypassSecurityTrustHtml(svg))
                ),
            { initialValue: '' as unknown as SafeHtml }
        );

        this.loadingSpinnerHtml = toSignal(
            this.iconService.fetchSvg(`${IMAGE_PATH}/loading-spinner.svg`)
                .pipe(
                    map(svg => this.domSanitizer.bypassSecurityTrustHtml(svg))
                ),
            { initialValue: '' as unknown as SafeHtml }
        );

        this.iconUrlState = toSignal(
            toObservable(this.iconUrl)
                .pipe(switchMap(value => this.toIconUrlStateObservable(
                    isIconUrlConfig(value) ? value.url : value
                ))),
            {
                initialValue: {
                    isLoading: false,
                    isLoadingSpinnerVisible: false,
                    url: undefined
                } as UrlState
            }
        );

        this.resolvedIconUrl = computed(() => this.iconUrlState().url);
        this.isImageLoading = linkedSignal(() => this.resolvedIconUrl() !== undefined);
        this.isLoading = computed(() =>
            this.iconUrlState().isLoading || this.svgLoadingState().isLoading || this.isImageLoading()
        );
        this.isLoadingSpinnerVisible = computed(() =>
            this.shouldDisplayLoadingSpinner()
            && (this.iconUrlState().isLoadingSpinnerVisible || this.svgLoadingState().isLoadingSpinnerVisible)
        );

        effect(() => {
            this.loadingChange.emit(this.isLoading());
        });
    }

    public getHostElement(): ElementRef {
        return this.hostElement;
    }

    protected onImageLoadFinished(): void {
        this.isImageLoading.set(false);
    }

    private toIconUrlStateObservable(value: IconUrlInput): Observable<UrlState> {
        if (isObservable(value)) {
            const loaded$ = (value as Observable<string | undefined>).pipe(
                map((url): UrlState => ({
                    isLoading: false,
                    isLoadingSpinnerVisible: false,
                    url
                })),
                catchError(() => of({
                    isLoading: false,
                    isLoadingSpinnerVisible: false,
                    url: undefined
                } as UrlState)),
                share()
            );
            return merge(
                timer(this.loadingSpinnerThreshold())
                    .pipe(
                        map((): UrlState => ({
                            isLoading: true,
                            isLoadingSpinnerVisible: true,
                            url: undefined
                        })),
                        takeUntil(loaded$)
                    ),
                loaded$
            )
                .pipe(startWith({
                    isLoading: true,
                    isLoadingSpinnerVisible: false,
                    url: undefined
                } as UrlState));
        }
        return of({
            isLoading: false,
            isLoadingSpinnerVisible: false,
            url: value === null ? undefined : value
        });
    }

    private getIconSizeFromCustomCSSProperty(): number {
        const rootStyles = getComputedStyle(document.documentElement);
        return Number(rootStyles.getPropertyValue(DEFAULT_ICON_SIZE_CUSTOM_CSS_PROPERTY_NAME)
            .replace('px', '')) || 0;
    }
}
